import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Mail, User, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const LeadMagnet: React.FC = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(false);

  // Envía el nombre/email al worker de Cloudflare (worker/index.js), que
  // manda la guía real por Resend y notifica el lead para seguimiento.
  // Solo existe en producción (Cloudflare) — en `npm run dev` este fetch
  // da 404 porque Vite no corre el Worker, así que también se muestra el
  // estado de error ahí (esperado en local, no es un bug).
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(false);
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const name = formData.get('name');
    const email = formData.get('email');

    try {
      const res = await fetch('/api/lead-magnet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email }),
      });
      if (!res.ok) throw new Error('request_failed');
      setIsSuccess(true);
    } catch {
      setError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section style={{ padding: 'clamp(4.5rem, 9vh, 7.5rem) 0', position: 'relative' }}>
      <div className="container" style={{ maxWidth: 900 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{
            position: 'relative', overflow: 'hidden',
            borderRadius: 32, padding: 'clamp(2rem, 5vw, 4rem)',
            background: 'linear-gradient(135deg, rgba(139,92,246,0.1), rgba(0,229,209,0.05))',
            border: '1px solid var(--border-light)',
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem',
            alignItems: 'center'
          }}
        >
          {/* Background Elements */}
          <div style={{ position: 'absolute', top: -100, left: -100, width: 300, height: 300, background: '#8b5cf6', filter: 'blur(100px)', opacity: 0.15, borderRadius: '50%' }} />
          <div style={{ position: 'absolute', bottom: -100, right: -100, width: 300, height: 300, background: '#00e5d1', filter: 'blur(100px)', opacity: 0.1, borderRadius: '50%' }} />

          {/* Copy Side */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--accent-purple)', marginBottom: '1.2rem', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '6px 14px', borderRadius: 999, background: 'rgba(139,92,246,0.1)' }}>
              {t('leadmagnet.badge', 'Recurso Gratuito')}
            </div>
            
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 300, color: 'var(--text-primary)', margin: '0 0 1rem', lineHeight: 1.15 }}>
              {t('leadmagnet.title1', 'Guía: 5 mensajes de WhatsApp que ')}<span className="grad-text" style={{ fontWeight: 800 }}>{t('leadmagnet.title2', 'duplican tus citas')}</span>
            </h2>
            
            <p style={{ color: 'var(--text-secondary)', fontSize: '1.05rem', lineHeight: 1.6, marginBottom: '2rem' }}>
              {t('leadmagnet.desc', 'Descubre las plantillas exactas que usan las clínicas más rentables para confirmar citas, recuperar pacientes perdidos y vender tratamientos costosos por chat.')}
            </p>

            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {[
                t('leadmagnet.feat1', 'Plantilla de confirmación anti-cancelaciones'),
                t('leadmagnet.feat2', 'Mensaje de reactivación 6 meses'),
                t('leadmagnet.feat3', 'Script para presupuestos altos'),
                t('leadmagnet.feat4', 'Bienvenida a paciente nuevo'),
                t('leadmagnet.feat5', 'Seguimiento post-tratamiento (pide reseña)'),
              ].map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                  <CheckCircle size={18} color="var(--accent-teal)" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Form Side */}
          <div className="leadmagnet-form" style={{ position: 'relative', zIndex: 1, padding: '2.5rem', borderRadius: 24, backdropFilter: 'blur(10px)' }}>
            {!isSuccess ? (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                <h3 style={{ margin: '0 0 0.5rem', color: 'var(--text-primary)', fontSize: '1.2rem' }}>{t('leadmagnet.form_title', 'Descargar PDF Gratis')}</h3>

                <div style={{ position: 'relative' }}>
                  <User size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder={t('leadmagnet.placeholder_name', 'Tu nombre')}
                    className="leadmagnet-input"
                    style={{ width: '100%', padding: '1rem 1rem 1rem 2.8rem', borderRadius: 12, color: 'var(--text-primary)', fontFamily: 'var(--font-body)', outline: 'none' }}
                  />
                </div>

                <div style={{ position: 'relative' }}>
                  <Mail size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder={t('leadmagnet.placeholder_email', 'tu@clinica.com')}
                    className="leadmagnet-input"
                    style={{ width: '100%', padding: '1rem 1rem 1rem 2.8rem', borderRadius: 12, color: 'var(--text-primary)', fontFamily: 'var(--font-body)', outline: 'none' }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: '100%', padding: '1rem', borderRadius: 12, border: 'none',
                    background: 'var(--accent-purple)', color: '#fff', fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '1.05rem',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer', opacity: isSubmitting ? 0.7 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: '0.5rem',
                    transition: 'opacity 0.2s'
                  }}
                >
                  {isSubmitting ? t('leadmagnet.sending', 'Enviando...') : (
                    <>
                      {t('leadmagnet.btn', 'Quiero la guía')}
                      <Download size={18} />
                    </>
                  )}
                </button>
                {error && (
                  <p style={{ margin: 0, textAlign: 'center', fontSize: '0.85rem', color: '#f87171' }}>
                    {t('leadmagnet.error', 'No pudimos enviar la guía. Intenta de nuevo o escríbenos por WhatsApp.')}
                  </p>
                )}
                <p style={{ margin: 0, textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {t('leadmagnet.spam_notice', '100% gratis. Cero spam. Nos odiamos el spam.')}
                </p>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{ textAlign: 'center', padding: '2rem 1rem' }}
              >
                <div style={{ width: 64, height: 64, background: 'rgba(37,211,102,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', border: '1px solid rgba(37,211,102,0.3)' }}>
                  <CheckCircle size={32} color="#25D366" />
                </div>
                <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1rem', fontSize: '1.4rem' }}>{t('leadmagnet.success_title', '¡Guía enviada!')}</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.5, margin: 0 }}>
                  {t('leadmagnet.success_desc', 'Revisa tu bandeja de entrada (o la carpeta de promociones). El PDF ya está en camino.')}
                </p>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>

      <style>{`
        .leadmagnet-form { background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.05); }
        [data-theme='light'] .leadmagnet-form { background: rgba(255,255,255,0.75); border: 1px solid rgba(0,0,0,0.08); box-shadow: 0 20px 45px -25px rgba(0,0,0,0.2); }

        .leadmagnet-input { border: 1px solid var(--border-light); background: rgba(255,255,255,0.03); }
        [data-theme='light'] .leadmagnet-input { background: rgba(0,0,0,0.03); }
        .leadmagnet-input::placeholder { color: var(--text-muted); }
      `}</style>
    </section>
  );
};

export default LeadMagnet;
